'use client';

import { FC, useState, useEffect, useMemo, useRef } from 'react';
import { useCompetitionContext } from "../../providers/CompetitionContext";
import { useTeamContext } from '../../providers/TeamContext';
import { Player } from '@repo/types'; 
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { DriverCard } from "./driver-card";
import { PlayerList } from "./player-list";
import { LoadingSpinner } from "../../components/loading-spinner";
import ConfirmationModal from '../../components/confirm-modal';
import { Clover, Coins, Save, XCircle, Users, Pencil, Check, X, Flag, Bus, Infinity as InfinityIcon } from 'lucide-react';
import Actus from "../../components/news";
import { getCompetitionCopy } from "../../lib/competitionCopy";
import { chunkByRowSizes, generateBenchRowSizes, generatePyramidRowSizes } from './row-layout';

import { toast } from 'react-toastify';

import moment from 'moment';
import 'moment/locale/fr';
import {
  parseTeamName,
  sanitizeTeamNameInput,
  TEAM_NAME_MAX_LENGTH,
} from '@repo/api/team-name';
moment.locale('fr');

interface EditTeamProps {
  teamId: string | null | undefined;
  weekId: number;
}

type EditPageMode = 'edit' | 'selections' | 'transfers';
type TransferChange = { inId?: Player; outId: Player };
type HistoricTransfer = { inId: number; outId: number; datetime: string };

const EditTeam: FC<EditTeamProps> = ({ teamId, weekId }) => {
  const PLAYER_LIST_ID = 'fantasy-player-list';
  const { competitionConfig, competitionData } = useCompetitionContext();
  const copy = getCompetitionCopy(competitionConfig?.competitionFeed);
  const { 
    team, 
    removeDriverFromTeam, 
    availableBudget, 
    setTeamName, 
    driverSelectionCheck, 
    addDriverToTeam,
    setCaptain,
    fetchTeam,
    isLoading,
    updateLineup
  } = useTeamContext();

  const [teamNameLocal, setTeamNameLocal] = useState('');
  const [isEditingTeamName, setIsEditingTeamName] = useState(false);
  // State for confirmation modal
  const [showCaptainUpdateButton, setShowCaptainUpdateButton] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalConfig, setModalConfig] = useState<{
    title: string;
    message: string;
    onConfirm: () => void;
    confirmText: string;
    cancelText?: string;
  }>({
    title: '',
    message: '',
    onConfirm: () => {},
    confirmText: '',
    cancelText: 'non'
  });

  const deadlineWeek = competitionData.deadlineInfo?.deadlineWeek || 0;
  const isEditWeek = Boolean(team.weekId && team.weekId === deadlineWeek);

  const transferModeSetting = competitionConfig?.transfersAllowed;
  const transferWindowWeeks = Array.isArray(competitionConfig?.transfersWindow)
    ? competitionConfig.transfersWindow
    : [];
  const isTransferWindowWeek = transferWindowWeeks.includes(deadlineWeek);

  const rawTransferLimit =
    typeof competitionConfig?.transfers === 'number'
      ? competitionConfig.transfers
      : typeof transferModeSetting === 'number'
        ? transferModeSetting
        : -1;
  const hasTransferLimit = Number.isFinite(rawTransferLimit) && rawTransferLimit >= 0;
  const transferLimit = hasTransferLimit ? rawTransferLimit : null;
  const transfersUnlimited = !hasTransferLimit;
  const transferLimitLabel = transfersUnlimited ? 'illimité' : String(transferLimit);

  const transfersAllowedNow = !isEditWeek && (
    transferModeSetting === 'competition' ||
    (transferModeSetting === 'window' && isTransferWindowWeek) ||
    typeof transferModeSetting === 'number'
  );
  const canOpenTransfers = transfersAllowedNow;
  const restrictActiveTransfers = transferModeSetting === 'window';
  const [pageMode, setPageMode] = useState<EditPageMode>('selections');
  const isEditState = pageMode === 'edit';
  const isSelectionsState = pageMode === 'selections';
  const isTransfersState = pageMode === 'transfers';
  const benchSize = Number(competitionConfig?.benchSize || 0);
  const hasBench = benchSize > 0;
  const canEditRoster = isEditState || isTransfersState;
  const canSwapLineup = hasBench && (isEditState || isSelectionsState);
  const canUseBoosters = isEditState || isSelectionsState;

  // New state to track transfer changes (an array of transfer objects)
  const [transferChanges, setTransferChanges] = useState<TransferChange[]>([]);
  // State to track historic transfers from the API
  const [historicTransfers, setHistoricTransfers] = useState<HistoricTransfer[]>([]);
  const [lineupSelection, setLineupSelection] = useState<{ player: Player; from: 'starting' | 'bench' } | null>(null);
  const [saveFutureWeeksSelection, setSaveFutureWeeksSelection] = useState(true);
  const initialLineupRef = useRef<{ startingIds: number[]; benchIds: number[] } | null>(null);
  const transferDraftBaselineRef = useRef<{ starting: Player[]; bench: Player[] } | null>(null);
  const benchContainerRef = useRef<HTMLDivElement | null>(null);
  const [isCompactBench, setIsCompactBench] = useState(false);

  useEffect(() => {
    const deadlineInfo = competitionData.deadlineInfo;
    if (!deadlineInfo?.deadlineDate || deadlineInfo.deadlineWeek === null) {
      return;
    }

    // Parse the deadline date in UTC and convert it to the local timezone
    const deadlineLocal = moment.utc(deadlineInfo.deadlineDate).local();
    if (!deadlineLocal.isValid()) {
      return;
    }

    const now = moment();
    
    // Calculate the delay in milliseconds until the deadline
    const delay = deadlineLocal.diff(now);

    if (delay > 0) {
      const timer = setTimeout(() => {
        // Optionally, notify the user before reloading
        console.log("Deadline reached, reloading the page...", delay, deadlineLocal, now);
        // window.location.reload();
        //todo use goToNextWeek which should become part of CompetitionContext
      }, delay);

      // Clean up the timer if the component unmounts
      return () => clearTimeout(timer);
    } else {
      // If the deadline has already passed, reload immediately or handle accordingly
      window.location.reload();
    }
  }, [competitionData]);

  useEffect(() => {
    setTeamNameLocal(team.teamName);
  }, [team.teamName]);

  useEffect(() => {
    const node = benchContainerRef.current;
    if (!node || typeof ResizeObserver === 'undefined') return;

    const updateCompactBench = (width: number) => {
      setIsCompactBench(width < 400);
    };

    updateCompactBench(node.getBoundingClientRect().width);

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      updateCompactBench(entry.contentRect.width);
    });

    observer.observe(node);

    return () => observer.disconnect();
  }, []);

  // Effect to process historic transfers when team data loads
  useEffect(() => {
    // Check if team data contains transfers information
    if (team && 'transfers' in team) {
      // Handle single transfer object case
      if (team.transfers && !Array.isArray(team.transfers)) {
        setHistoricTransfers([{
          inId: team.transfers.inId,
          outId: team.transfers.outId, 
          datetime: team.transfers.datetime
        }]);
      } 
      // Handle array of transfers case
      else if (Array.isArray(team.transfers) && team.transfers.length > 0) {
        setHistoricTransfers(team.transfers.map(transfer => ({
          inId: transfer.inId,
          outId: transfer.outId,
          datetime: transfer.datetime
        })));
      } else {
        setHistoricTransfers([]);
      }
    } else {
      setHistoricTransfers([]);
    }
  }, [team]);

  useEffect(() => {
    if (!team?.starting || !team?.bench) return;
    if (!('roster' in team) || !team.roster) return;
    const sortedStartingIds = team.starting.map((p) => p.id).sort((a, b) => a - b);
    const sortedBenchIds = team.bench.map((p) => p.id).sort((a, b) => a - b);
    initialLineupRef.current = {
      startingIds: sortedStartingIds,
      benchIds: sortedBenchIds
    };
  }, [team.roster, team.id, weekId]);

  useEffect(() => {
    if (!lineupSelection) return;
    const stillExists = [...team.starting, ...team.bench].some(
      (p) => p.id === lineupSelection.player.id
    );
    if (!stillExists) {
      setLineupSelection(null);
    }
  }, [team.starting, team.bench, lineupSelection]);

  useEffect(() => {
    if (isEditWeek) {
      setPageMode('edit');
      return;
    }

    setPageMode((currentMode) => {
      if (currentMode === 'transfers' && canOpenTransfers) {
        return currentMode;
      }
      return 'selections';
    });
  }, [isEditWeek, canOpenTransfers, weekId, team.id]);

  useEffect(() => {
    if (!canSwapLineup && lineupSelection) {
      setLineupSelection(null);
    }
  }, [canSwapLineup, lineupSelection]);

  const setCurrentLineupAsInitial = () => {
    const sortedStartingIds = team.starting.map((p) => p.id).sort((a, b) => a - b);
    const sortedBenchIds = team.bench.map((p) => p.id).sort((a, b) => a - b);
    initialLineupRef.current = {
      startingIds: sortedStartingIds,
      benchIds: sortedBenchIds
    };
  };

  const snapshotTransferDraftBaseline = () => {
    transferDraftBaselineRef.current = {
      starting: team.starting.map((p) => ({ ...p })),
      bench: team.bench.map((p) => ({ ...p }))
    };
  };

  const revertUnsavedTransferDraft = (options?: { keepBaseline?: boolean }) => {
    const baseline = transferDraftBaselineRef.current;
    if (!baseline) {
      setTransferChanges([]);
      return;
    }

    updateLineup(
      baseline.starting.map((p) => ({ ...p, pos: 1 })),
      baseline.bench.map((p) => ({ ...p, pos: 0 }))
    );
    setTransferChanges([]);
    if (!options?.keepBaseline) {
      transferDraftBaselineRef.current = null;
    }
  };

  useEffect(() => {
    if (pageMode === 'transfers') return;

    const baseline = transferDraftBaselineRef.current;
    if (!baseline) return;

    if (transferChanges.length > 0) {
      updateLineup(
        baseline.starting.map((p) => ({ ...p, pos: 1 })),
        baseline.bench.map((p) => ({ ...p, pos: 0 }))
      );
      setTransferChanges([]);
    }

    transferDraftBaselineRef.current = null;
  }, [pageMode, transferChanges.length, updateLineup]);

  const handleResetTransfers = () => {
    // Show confirmation modal for resetting transfers
    setModalConfig({
      title: 'Annuler transferts',
      message: 'Es-tu sûr(e) de vouloir annuler tous les transferts ? Cette action est irréversible.',
      onConfirm: () => {
        revertUnsavedTransferDraft({ keepBaseline: true });
        setLineupSelection(null);
        toast.info("Transferts annulés.");
      },
      confirmText: 'Reset'
    });
    setIsModalOpen(true);
  };

  const performUpdateTeam = async () => {
    if (!teamId) {
      toast.error('Team ID manquant.');
      return;
    }

    const parsedTeamId = Number(teamId);
    if (!Number.isFinite(parsedTeamId)) {
      toast.error('Team ID invalide.');
      return;
    }

    await putTeamPayload(
      '/api/team/edit',
      {
        teamId: parsedTeamId,
        starting: team.starting.map((p) => p.id),
        bench: team.bench.map((p) => p.id),
        prediction: team.prediction,
        extra: team.extra
      },
      'Équipe mise à jour.',
      () => {
        setCurrentLineupAsInitial();
        setShowCaptainUpdateButton(false);
      }
    );
  };

  const handleSaveTeamName = async () => {
    const parsedTeamName = parseTeamName(teamNameLocal);
    if (!parsedTeamName.ok) {
      toast.error(parsedTeamName.message);
      return;
    }

    const nextTeamName = parsedTeamName.value;

    if (!teamId) {
      toast.error('Team ID manquant.');
      return;
    }

    const parsedTeamId = Number(teamId);
    if (!Number.isFinite(parsedTeamId)) {
      toast.error('Team ID invalide.');
      return;
    }

    try {
      const res = await fetch('/api/team/name', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          teamId: parsedTeamId,
          name: nextTeamName
        }),
      });

      if (res.ok) {
        const body = await res.json().catch(() => null);
        setTeamNameLocal(nextTeamName);
        setTeamName(nextTeamName);
        setIsEditingTeamName(false);
        toast.success(body?.message || 'Nom de l’équipe mis à jour.');
        fetchTeam(teamId);
        return;
      }

      const error = await res.json().catch(() => null);
      toast.error(error?.message || 'Erreur. Le nom de l’équipe n’a pas été enregistré.');
    } catch (error) {
      console.error('Error updating team name:', error);
      toast.error('Une erreur s\'est produite lors de l\'enregistrement du nom.');
    }
  };

  const handleCancelTeamNameEdit = () => {
    setTeamNameLocal(team.teamName);
    setTeamName(team.teamName);
    setIsEditingTeamName(false);
  };

  const activateBooster = async () => {
    const payload = {
      teamId,
      boosterType: 'captain',
      captain: team.starting[0]?.id,
      weekId: weekId
    };
    
    try {
      const res = await fetch('/api/team/booster', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      
      if (res.ok) {
        toast.success('Joker utilisé!');
        
        // Refresh team data from the server to ensure captain changes are reflected
        if (teamId) {
          fetchTeam(teamId);
        }
      } else {
        const error = await res.json();
        console.log('toast', error.message)
        //tost not showing
        toast.error(error.message || 'Error.');
      }
    } catch (error) {
      console.error('Error activateBooster:', error);
      toast.error('Could not activate the booster');
    }
  }

  const cancelBooster = async () => {
    const appliedWeek = team.tripleCaptain != null ? Number(team.tripleCaptain) : weekId;
    const appliedCaptain = team.starting.find((p) => p.cap === 1)?.id ?? team.starting[0]?.id;
    const payload = {
      teamId,
      boosterType: 'captain',
      captain: appliedCaptain,
      weekId: appliedWeek
    };
    
    try {
      const res = await fetch('/api/team/cancelBooster', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      
      if (res.ok) {
        toast.success('Joker annulé!');
        
        // Refresh team data from the server to ensure captain changes are reflected
        if (teamId) {
          fetchTeam(teamId);
        }
      } else {
        const error = await res.json();
        console.log('toast', error.message)
        //tost not showing
        toast.error(error.message || 'Error.');
      }
    } catch (error) {
      console.error('Error cancelBooster:', error);
      toast.error('Could not cancel the booster');
    }
  }

  const getApiErrorMessage = async (response: Response, fallback: string) => {
    try {
      const payload = await response.json();
      if (payload?.message) return payload.message as string;
      if (payload?.error) return payload.error as string;
      return fallback;
    } catch {
      return fallback;
    }
  };

  const putTeamPayload = async (
    endpoint: string,
    payload: Record<string, unknown>,
    successMessage: string,
    onSuccess?: () => void
  ) => {
    try {
      const res = await fetch(endpoint, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        onSuccess?.();
        toast.success(successMessage);
        if (teamId) {
          fetchTeam(teamId);
        }
        return;
      }

      const fallbackMessage = `Erreur. Les changements n'ont pas été enregistrés`;
      const errorMessage = await getApiErrorMessage(res, fallbackMessage);
      toast.error(errorMessage);
    } catch (error) {
      console.error('Error saving team payload:', error);
      toast.error(`Une erreur s'est produite lors de l'enregistrement`);
    }
  };

  const postTransferPayload = async (
    payload: Record<string, unknown>,
    successMessage: string,
    onSuccess?: () => void
  ) => {
    try {
      const res = await fetch('/api/team/transfer', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        toast.success(successMessage);
        onSuccess?.();
        if (teamId) {
          fetchTeam(teamId);
        }
        return;
      }

      const fallbackMessage = `Erreur. Les changements n'ont pas été enregistrés`;
      const errorMessage = await getApiErrorMessage(res, fallbackMessage);
      toast.error(errorMessage);
    } catch (error) {
      console.error('Error saving team transfer payload:', error);
      toast.error(`Une erreur s'est produite lors de l'enregistrement`);
    }
  };

  const performSelectionsUpdate = async () => {
    if (!lineupHasChanges) {
      toast.error('Aucun changement de sélection à enregistrer.');
      return;
    }

    if (!teamId) {
      toast.error('Team ID manquant.');
      return;
    }

    const endpoint = saveFutureWeeksSelection
      ? `/api/team/${teamId}`
      : `/api/team/${teamId}/week/${weekId}`;

    await putTeamPayload(
      endpoint,
      {
        starting: team.starting.map((p) => p.id),
        bench: team.bench.map((p) => p.id),
      },
      saveFutureWeeksSelection
        ? `Sélections sauvegardées pour toutes les ${copy.raceLabelPlural}.`
        : `${copy.raceLabelTitle} ${weekId} sauvegardée.`,
      () => {
        setCurrentLineupAsInitial();
        setShowCaptainUpdateButton(false);
        setLineupSelection(null);
      }
    );
  };

  const performTransfersUpdate = async () => {
    const totalHistoricTransfers = historicTransfers.length;
    const totalTransfers = totalHistoricTransfers + transferChanges.length;
    const hasTransferChanges = transferChanges.length > 0;

    if (!hasTransferChanges) {
      toast.error(`Aucun transfert à enregistrer.`);
      return;
    }

    if (!transfersUnlimited && transferLimit !== null && totalTransfers > transferLimit) {
      toast.error(`Je hebt het maximum aantal toegestane transfers (${transferLimitLabel}) overschreden.`);
      return;
    }

    if (transferChanges.some((t) => !t.inId)) {
      toast.error(`Complète d'abord tous les transferts.`);
      return;
    }

    if (!teamId) {
      toast.error('Team ID manquant.');
      return;
    }

    await postTransferPayload(
      {
        teamId,
        transfers: transferChanges.map((t) => ({
          inId: t.inId?.id,
          outId: t.outId.id,
        })),
        starting: team.starting.map((p) => p.id),
        bench: team.bench.map((p) => p.id)
      },
      'Transferts sauvegardés.',
      () => {
        setTransferChanges([]);
        setCurrentLineupAsInitial();
        snapshotTransferDraftBaseline();
      }
    );
  };

  const handleTransfersSaveClick = () => {
    setModalConfig({
      title: `Enregistrer transferts`,
      message: `Es-tu sûr(e) de vouloir enregistrer tes transferts ? Les transferts sont définitifs et cette action est irréversible.`,
      onConfirm: performTransfersUpdate,
      confirmText: `Enregistrer`
    });
    setIsModalOpen(true);
  };

  const handleEnableTransfers = () => {
    if (!canOpenTransfers) return;

    if (lineupHasChanges) {
      toast.info('Enregistre d’abord tes sélections avant d’activer les transferts.');
      return;
    }

    snapshotTransferDraftBaseline();
    setLineupSelection(null);
    setPageMode('transfers');
  };

  const handleBackToSelections = () => {
    if (transferChanges.length > 0) {
      revertUnsavedTransferDraft();
    } else {
      transferDraftBaselineRef.current = null;
    }
    setLineupSelection(null);
    setPageMode('selections');
  };

  const lineupSize = competitionConfig.lineupSize;
  const shouldVerticallyCenterStarting = lineupSize === 15 && !hasBench;
  // Calculate total selections needed
  const totalSelections = lineupSize + benchSize;

  // Current selected drivers
  const selectedDrivers = [...team?.starting, ...team?.bench].map((driver: any) => ({
    ...driver,
    club: competitionData.clubs.find((club: any) => club.id === driver.clubId)
  })).sort((a: any, b: any) => b.cap - a.cap);

  const isCaptainBoosterAllowed = competitionConfig.captainBooster === true;
  const boosterWeek = team.tripleCaptain != null ? Number(team.tripleCaptain) : null;
  const boosterUsedThisWeek = boosterWeek === weekId;
  const boosterUsedOtherWeek = boosterWeek != null && boosterWeek !== weekId;
  const hasCaptainBoosterEnabled = boosterUsedThisWeek;

  const startingSlots = [
    ...team.starting,
    ...Array(Math.max(lineupSize - team.starting.length, 0)).fill(undefined),
  ];

  const benchSlots = hasBench ? [
    ...team.bench,
    ...Array(Math.max(benchSize - team.bench.length, 0)).fill(undefined),
  ] : [];

  const startingRowSizes = generatePyramidRowSizes(lineupSize);
  const benchRowSizes = hasBench ? generateBenchRowSizes(benchSize) : [];
  const startingRowChunks = chunkByRowSizes(startingSlots, startingRowSizes);
  const benchRowChunks = hasBench ? chunkByRowSizes(benchSlots, benchRowSizes) : [];

  const handleRemoveDriver = (driver: Player) => {
    if (!canEditRoster) return;
    if (isTransfersState && restrictActiveTransfers && driver.active === 1) return;

    if (isTransfersState && team.weekId && team.weekId < deadlineWeek) {
      const totalHistoric = historicTransfers.length;
      if (!transfersUnlimited && transferLimit !== null && transferChanges.length + 1 + totalHistoric > transferLimit) {
        toast.error(`Maximum transfers (${transferLimitLabel}) bereikt`);
        return;
      }
      setTransferChanges(prev => [...prev, { outId: driver }]);
    }
    removeDriverFromTeam(driver);
  };

  const applyLineupUpdate = (starting: Player[], bench: Player[]) => {
    updateLineup(
      starting.map((p) => ({ ...p, pos: 1 })),
      bench.map((p) => ({ ...p, pos: 0 }))
    );
  };

  const movePlayerToLineup = (player: Player, target: 'starting' | 'bench') => {
    const inStarting = team.starting.some((p) => p.id === player.id);
    const inBench = team.bench.some((p) => p.id === player.id);

    if (target === 'starting') {
      if (inStarting || team.starting.length >= lineupSize) return;
      const nextStarting = [...team.starting, player];
      const nextBench = team.bench.filter((p) => p.id !== player.id);
      applyLineupUpdate(nextStarting, nextBench);
      return;
    }

    if (inBench || team.bench.length >= benchSize) return;
    const nextStarting = team.starting.filter((p) => p.id !== player.id);
    const nextBench = [...team.bench, player];
    applyLineupUpdate(nextStarting, nextBench);
  };

  const swapLineupPlayers = (first: Player, second: Player) => {
    const firstInStarting = team.starting.some((p) => p.id === first.id);
    const secondInStarting = team.starting.some((p) => p.id === second.id);
    if (firstInStarting === secondInStarting) return;

    const nextStarting = team.starting.map((p) => {
      if (p.id === first.id) return second;
      if (p.id === second.id) return first;
      return p;
    });
    const nextBench = team.bench.map((p) => {
      if (p.id === first.id) return second;
      if (p.id === second.id) return first;
      return p;
    });

    applyLineupUpdate(nextStarting, nextBench);
  };

  const handleLineupSelect = (lineup: 'starting' | 'bench', driver?: Player) => {
    if (!canSwapLineup) return;

    if (!driver) {
      if (!lineupSelection) return;
      if (lineupSelection.from === lineup) {
        setLineupSelection(null);
        return;
      }
      movePlayerToLineup(lineupSelection.player, lineup);
      setLineupSelection(null);
      return;
    }

    if (!lineupSelection) {
      setLineupSelection({ player: driver, from: lineup });
      return;
    }

    if (lineupSelection.player.id === driver.id) {
      setLineupSelection(null);
      return;
    }

    if (lineupSelection.from === lineup) {
      setLineupSelection({ player: driver, from: lineup });
      return;
    }

    swapLineupPlayers(lineupSelection.player, driver);
    setLineupSelection(null);
  };

  const scrollToPlayerList = () => {
    document.getElementById(PLAYER_LIST_ID)?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  };

  const handleStartingCardSelect = (driver?: Player) => {
    if (canSwapLineup) {
      handleLineupSelect('starting', driver);
    }
    scrollToPlayerList();
  };

  const handleBenchCardSelect = (driver?: Player) => {
    if (canSwapLineup) {
      handleLineupSelect('bench', driver);
    }
    scrollToPlayerList();
  };
  
  const handleDriverSelection = (driver: Player) => {
    if (!canEditRoster) return;

    addDriverToTeam(driver);

    if (isTransfersState && team.weekId && team.weekId < deadlineWeek) {
      setTransferChanges((prev) => {
        const newChanges = [...prev];
        const unpairedIndex = newChanges.findIndex((t) => !t.inId);
        if (unpairedIndex !== -1) {
          newChanges[unpairedIndex] = {
            ...newChanges[unpairedIndex],
            outId: newChanges[unpairedIndex]?.outId!,
            inId: driver,
          };
        }
        return newChanges;
      });
    }
  };  

  const arraysEqual = (a: number[], b: number[]) => (
    a.length === b.length && a.every((value, index) => value === b[index])
  );

  const lineupHasChanges = useMemo(() => {
    const initial = initialLineupRef.current;
    if (!initial) return false;
    const currentStartingIds = team.starting.map((p) => p.id).sort((a, b) => a - b);
    const currentBenchIds = team.bench.map((p) => p.id).sort((a, b) => a - b);
    return (
      !arraysEqual(initial.startingIds, currentStartingIds) ||
      !arraysEqual(initial.benchIds, currentBenchIds)
    );
  }, [team.starting, team.bench]);

  const totalTransfersCount = historicTransfers.length + transferChanges.length;
  const transferLimitReached = isTransfersState && !transfersUnlimited && transferLimit !== null && totalTransfersCount >= transferLimit;
  const transferWindowLabel = transferWindowWeeks.length > 0 ? transferWindowWeeks.join(', ') : '';
  const transferInfoTitle = isEditState
    ? 'Mode édition'
    : isTransfersState
      ? 'Mode transferts'
      : 'Mode sélections';
  const transferInfoMessage = isEditState
    ? `Tu peux modifier toute ton équipe jusqu'au départ de la première ${copy.raceLabelSingular}.`
    : isTransfersState
      ? (transferModeSetting === 'window'
          ? (transferWindowLabel
              ? `Pendant les semaines ${transferWindowLabel}, tu peux remplacer les coureurs éligibles aux transferts.`
              : `Transferts disponibles pendant les fenêtres prévues.`)
          : `Tu peux effectuer des transferts${transfersUnlimited ? '' : ` (max ${transferLimitLabel} au total)`}.`)
      : (canOpenTransfers
          ? (hasBench
              ? 'Ajuste tes partants/remplaçants, puis active les transferts si tu dois changer des coureurs.'
              : 'Active les transferts si tu dois changer des coureurs.')
          : (transferModeSetting === 'window' && transferWindowLabel
              ? `Transferts disponibles uniquement pendant les semaines ${transferWindowLabel}.`
              : (hasBench
                  ? `Ajuste uniquement tes partants/remplaçants pour cette semaine.`
                  : `Aucun changement de coureurs possible pour cette semaine.`)));

  const transferTaxPercentage = competitionConfig.transferTaxPercentage || 0;
  const { totalTransfersCost, effectiveAvailableBudget } = useMemo(() => {
    let cost : number = 0;

    // Historic transfers
    for (const transfer of historicTransfers) {
      const incomingPlayer = competitionData.players.find((p:any) => p.id === transfer.inId);
      if (incomingPlayer) {
        cost += incomingPlayer.value * transferTaxPercentage * 10000;;
      }
    }

    // Pending transfer changes
    for (const transfer of transferChanges) {
      if (transfer.inId) {
        cost += Number(transfer.inId.value) * transferTaxPercentage * 10000;
      }
    }

    return {
      totalTransfersCost: cost,
      effectiveAvailableBudget: availableBudget - cost
    };
  }, [historicTransfers, transferChanges, competitionData.players, availableBudget, transferTaxPercentage]);

  const formatCurrencyInMillions = (amount: number) => (
    `${(amount / 1000000).toLocaleString('nl-NL', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}M`
  );

  const hasCompleteRoster = selectedDrivers.length >= totalSelections;
  const baseSaveDisabled = !hasCompleteRoster;
  const transferSaveDisabled = baseSaveDisabled || transferChanges.length === 0;
  const selectionSaveDisabled = baseSaveDisabled || !lineupHasChanges;
  const shouldShowTransferBudget = isEditState || isTransfersState;

  if (team == null || isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <><div className="grid grid-cols-1 md:grid-cols-2 gap-2">
      {!selectedDrivers && <LoadingSpinner />}
      
      {/* Confirmation Modal */}
      <ConfirmationModal 
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onConfirm={modalConfig.onConfirm}
        title={modalConfig.title}
        message={modalConfig.message}
        confirmText={modalConfig.confirmText}
        cancelText={modalConfig.cancelText}
      />
      
      {/* Left column */}
      <div className="">
        <div className={`bg-[rgb(0_61_96)] text-white p-4 rounded-t-lg`}>
          <div className="flex items-center justify-between gap-2">
            {isEditingTeamName ? (
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <Users className="w-4 h-4 flex-shrink-0" />
                <Input
                  id="teamName"
                  onChange={(e) => {
                    const nextValue = sanitizeTeamNameInput(e.target.value);
                    setTeamNameLocal(nextValue);
                    setTeamName(nextValue);
                  }}
                  type="text"
                  value={teamNameLocal}
                  maxLength={TEAM_NAME_MAX_LENGTH}
                  placeholder="Nom de votre équipe"
                  className="max-w-sm text-black h-7 text-sm"
                />
                <button
                  onClick={handleSaveTeamName}
                  disabled={!parseTeamName(teamNameLocal).ok}
                  className="p-1 rounded hover:bg-white/20 disabled:opacity-40 disabled:cursor-not-allowed"
                  title="Enregistrer"
                >
                  <Check className="w-4 h-4" />
                </button>
                <button
                  onClick={handleCancelTeamNameEdit}
                  className="p-1 rounded hover:bg-white/20"
                  title="Annuler"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-2 min-w-0">
                  <Users className="w-4 h-4 flex-shrink-0" />
                  <h1 className="font-extra text-sm truncate">{teamNameLocal || "Mon équipe"}</h1>
                </div>
                <button
                  onClick={() => setIsEditingTeamName(true)}
                  className="p-1 rounded hover:bg-white/20 flex-shrink-0"
                  title="Modifier le nom"
                >
                  <Pencil className="w-3.5 h-3.5" />
                </button>
              </>
            )}
          </div>
        </div>
        <div className="bg-white w-full rounded-b-lg p-4">
        <div className="max-w-[614px] mx-auto border border-gpspel-grey-dark bg-gpspel-grey rounded-lg shadow-sm mx-4 overflow-hidden">
          <div className="flex items-center justify-between gap-2 p-4">
            <div className="min-w-0">
              <div className="font-bold text-sm truncate">{transferInfoTitle}</div>
              <div className="text-xs text-gray-500 mt-0.5">{transferInfoMessage}</div>
            </div>
            <div className="flex-shrink-0">
              {isSelectionsState && canOpenTransfers && (
                <Button
                  onClick={handleEnableTransfers}
                  variant="secondary"
                  className="font-bold inline-flex items-center gap-2 text-sm"
                >
                  <Coins className="h-4 w-4" />
                  Activer transferts
                </Button>
              )}
              {isTransfersState && (
                <Button
                  onClick={handleBackToSelections}
                  variant="secondary"
                  className="font-bold text-sm"
                >
                  Retour sélections
                </Button>
              )}
            </div>
          </div>

          {/* Captain Booster Section */}
          {canUseBoosters && isCaptainBoosterAllowed && (
            <>
              <div className="border-t border-gpspel-grey-dark" />
              <div className="px-4 py-3">
                <div className="flex items-center gap-2 mb-2">
                  <Clover className="h-4 w-4 text-[rgb(0_61_96)] flex-shrink-0" />
                  <Label htmlFor="captain" className="font-bold text-sm m-0">
                    {boosterUsedOtherWeek
                      ? `Joker déjà utilisé pour ${copy.raceLabelWithArticle} n°${boosterWeek}.`
                      : `Joker - doubler les points de ${copy.raceLabelWithArticle} n°${weekId}`}
                  </Label>
                </div>
                {!boosterUsedOtherWeek && (
                <div className="flex items-center gap-3">
                  <select
                    id="captain"
                    value={selectedDrivers[0]?.id || ""}
                    disabled={hasCaptainBoosterEnabled}
                    onChange={(e) => {
                      const newCaptainId = parseInt(e.target.value);
                      const newCaptain = selectedDrivers.find(driver => driver && driver.id === newCaptainId);
                      if (newCaptain) {
                        setCaptain(newCaptain);
                      }
                    }}
                    className="flex-1 border border-gray-300 bg-white rounded-md shadow-sm py-2 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[rgb(0_61_96)] focus:border-transparent transition disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
                  >
                  {selectedDrivers.map((driver) =>
                    driver ? (
                      <option key={driver.id} value={driver.id}>
                        {driver.short}
                      </option>
                    ) : null
                  )}
                  </select>
                  <div className="flex-shrink-0">
                    {hasCaptainBoosterEnabled ? (
                      <Button
                        onClick={cancelBooster}
                        disabled={!hasCompleteRoster}
                        variant="default"
                        className="inline-flex items-center gap-1.5 font-bold text-sm bg-red-600 hover:bg-red-700 text-white rounded-md px-4 py-2 disabled:bg-gray-300 disabled:cursor-not-allowed transition"
                      >
                        <XCircle className="h-4 w-4" /> Annuler
                      </Button>
                    ) : (
                      <Button
                        onClick={activateBooster}
                        disabled={!hasCompleteRoster}
                        variant="default"
                        className="inline-flex items-center gap-1.5 font-bold text-sm bg-[rgb(0_61_96)] hover:bg-[rgb(0_50_80)] text-white rounded-md px-4 py-2 disabled:bg-gray-300 disabled:cursor-not-allowed transition"
                      >
                        <Clover className="h-4 w-4" /> Utiliser
                      </Button>
                    )}
                  </div>
                </div>
                )}
              </div>
            </>
          )}

          {/* Budget Section */}
          {shouldShowTransferBudget && (
            <>
              <div className="border-t border-gpspel-grey-dark" />
              <div className="px-4 py-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">Budget restant</span>
                  <span className="text-lg font-extrabold text-[rgb(0_61_96)]">
                    {formatCurrencyInMillions(effectiveAvailableBudget)}
                  </span>
                </div>
                {isTransfersState && (
                  <div className="mt-1 flex items-center justify-between text-xs text-gray-500">
                    <span>Taxes (10%)</span>
                    <span className="font-medium">{formatCurrencyInMillions(totalTransfersCost)}</span>
                  </div>
                )}
              </div>
            </>
          )}

          {/* Transfers Section */}
          {isTransfersState && (
            <>
              <div className="border-t border-gpspel-grey-dark" />
              <div className="px-4 py-3">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="font-bold text-sm">Transferts</h2>
                  <span className="inline-flex items-center gap-1 bg-[rgb(0_61_96)] text-white text-xs font-bold rounded-full px-3 py-1">
                    {totalTransfersCount} / {transferLimitLabel}
                  </span>
                </div>

                {historicTransfers.length === 0 && transferChanges.length === 0 && (
                  <p className="text-xs text-gray-500 text-center py-3">Aucun transfert pour le moment.</p>
                )}

                {/* Historic Transfers Table */}
                {historicTransfers.length > 0 && (
                  <div className="mb-4">
                    <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Historique</h3>
                    <div className="rounded-lg overflow-hidden border border-gray-200">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wider">
                            <th className="px-3 py-2 text-left font-medium w-8">#</th>
                            <th className="px-3 py-2 text-left font-medium">Sortie</th>
                            <th className="px-3 py-2 text-left font-medium">Entrée</th>
                            <th className="px-3 py-2 text-right font-medium">Date</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-100">
                          {historicTransfers.map((transfer, index) => {
                            const outPlayer = competitionData.players.find((p: Player) => p.id === transfer.outId);
                            const inPlayer = competitionData.players.find((p: Player) => p.id === transfer.inId);
                            const outgoingName = outPlayer?.name || `ID: ${transfer.outId}`;
                            const incomingName = inPlayer?.name || `ID: ${transfer.inId}`;
                            const transferDate = new Date(transfer.datetime);

                            return (
                              <tr key={`historic-${index}`} className="hover:bg-gray-50 transition-colors">
                                <td className="px-3 py-2 text-xs text-gray-400 font-medium">{index + 1}</td>
                                <td className="px-3 py-2 text-xs font-medium text-red-600">{outgoingName}</td>
                                <td className="px-3 py-2 text-xs font-medium text-green-600">{incomingName}</td>
                                <td className="px-3 py-2 text-xs text-gray-500 text-right">{transferDate.toLocaleDateString('nl-NL')}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Current Transfers Table */}
                {transferChanges.length > 0 && (
                  <div className="mb-3">
                    <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Transferts en cours</h3>
                    <div className="rounded-lg overflow-hidden border border-blue-200 ring-1 ring-blue-100">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-blue-50 text-gray-600 text-xs uppercase tracking-wider">
                            <th className="px-3 py-2 text-left font-medium w-8">#</th>
                            <th className="px-3 py-2 text-left font-medium">Sortie</th>
                            <th className="px-3 py-2 text-left font-medium">Entrée</th>
                            <th className="px-3 py-2 text-right font-medium">+/-</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-100">
                          {transferChanges.map((transfer, index) => {
                            const outgoingName = transfer.outId?.name || "N/A";
                            const incomingName = transfer.inId?.name || "-";
                            const diff =
                              transfer.inId && transfer.outId
                                ? Number(transfer.outId.value) - Number(transfer.inId.value)
                                : 0;
                            const diffText =
                              transfer.inId && transfer.outId
                                ? diff >= 0
                                  ? `+${diff}`
                                  : `${diff}`
                                : '-';
                            const diffColor = diff >= 0 ? "text-green-600 bg-green-50" : "text-red-600 bg-red-50";
                            return (
                              <tr key={index} className="hover:bg-gray-50 transition-colors">
                                <td className="px-3 py-2 text-xs text-gray-400 font-medium">{index + 1}</td>
                                <td className="px-3 py-2 text-xs font-medium text-red-600">{outgoingName}</td>
                                <td className="px-3 py-2 text-xs font-medium text-green-600">{incomingName}</td>
                                <td className="px-3 py-2 text-right">
                                  <span className={`inline-block text-xs font-bold rounded-full px-2 py-0.5 ${transfer.inId ? diffColor : "text-gray-400"}`}>
                                    {diffText}
                                  </span>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
                {/* Transfer Action Buttons */}
                <div className="flex items-center justify-between gap-3 pt-2 border-t border-gray-200 mt-3">
                  <Button
                    onClick={handleResetTransfers}
                    type="button"
                    disabled={transferChanges.length === 0}
                    variant="destructive"
                    className="inline-flex items-center gap-2 font-bold text-sm rounded-md px-4 py-2 disabled:opacity-40 disabled:cursor-not-allowed transition"
                  >
                    <XCircle className="w-4 h-4" />
                    Annuler
                  </Button>
                  <Button
                    onClick={handleTransfersSaveClick}
                    disabled={transferSaveDisabled}
                    variant="green"
                    className="inline-flex items-center gap-2 font-extrabold text-sm rounded-md px-5 py-2 disabled:opacity-40 disabled:cursor-not-allowed transition"
                  >
                    <Save className="w-4 h-4" />
                    Enregistrer
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
        </div>

        <div className="rounded-b-lg pt-0">
          {/* Starting Lineup */}
          <div className="px-4 pt-4 pb-1 flex items-center gap-2">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-[#003d60] text-white text-xs font-bold uppercase tracking-wide px-3 py-1">
              <Flag className="h-3.5 w-3.5" />
              Partants
            </span>
            <div className="flex-1 h-px bg-gray-200" />
          </div>
          <div 
            className={`mx-auto relative w-full h-auto bg-no-repeat bg-top bg-contain rounded-lg overflow-hidden ${
              shouldVerticallyCenterStarting
                ? 'flex min-h-[46rem] flex-col justify-center py-6'
                : 'pt-2 pb-10'
            }`}
            style={{
              backgroundSize: "cover",
              backgroundImage: "linear-gradient(to bottom, rgba(0,61,96,0.15), rgba(0,61,96,0.3)), url('https://ocnc-fantasy.b-cdn.net/files/road.jpg')"
            }}
          >
            {startingRowChunks.map((row, rowIndex) => (
              <div 
                key={`starting-${rowIndex}`}
                className={`flex justify-center gap-2 ${rowIndex && 'my-2'}`}
              >
                {row.map((driver, colIndex) => (
                  <DriverCard
                    key={colIndex}
                    driver={driver}
                    hasCaptainBoosterEnabled={isCaptainBoosterAllowed && hasCaptainBoosterEnabled}
                    isSelected={Boolean(driver && lineupSelection?.player.id === driver.id)}
                    onSwap={canSwapLineup ? () => handleLineupSelect('starting', driver as Player | undefined) : undefined}
                    swapLabel="vers le bus"
                    onSelect={() => handleStartingCardSelect(driver as Player | undefined)}
                    onRemove={
                      ((isTransfersState && restrictActiveTransfers && driver?.active === 1) || (isTransfersState && transferLimitReached)) ?
                        undefined :
                      driver && canEditRoster
                        ? () => handleRemoveDriver(driver as Player)
                        : undefined
                    }
                  />
                ))}
              </div>
            ))}
          </div>

          {hasBench && (
            <>
              <div className="px-4 pt-3 pb-1 flex items-center gap-2">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-[#0076a9] text-white text-xs font-bold uppercase tracking-wide px-3 py-1">
                  <Bus className="h-3.5 w-3.5" />
                  Remplaçants
                </span>
                <div className="flex-1 h-px bg-gray-200" />
              </div>
              <div 
                ref={benchContainerRef}
                className="
                  mx-auto relative
                  pt-2
                  pb-10
                  w-full 
                  h-auto
                  bg-no-repeat bg-top bg-contain
                  rounded-lg overflow-hidden
                "
                style={{
                  backgroundSize: "cover",
                  backgroundImage: "linear-gradient(to bottom, rgb(0, 22, 31), rgb(0, 43, 62))"
                }}
              >
                {benchRowChunks.map((row, rowIndex) => (
                  <div 
                    key={`bench-${rowIndex}`}
                    className={`flex justify-center ${isCompactBench ? 'gap-1 px-1' : 'gap-2 px-2'} ${rowIndex ? 'my-2' : ''}`}
                  >
                    {row.map((driver, colIndex) => (
                      <DriverCard
                        key={colIndex}
                        driver={driver}
                        compact={isCompactBench}
                        hasCaptainBoosterEnabled={isCaptainBoosterAllowed && hasCaptainBoosterEnabled}
                        isSelected={Boolean(driver && lineupSelection?.player.id === driver.id)}
                        onSwap={canSwapLineup ? () => handleLineupSelect('bench', driver as Player | undefined) : undefined}
                        swapLabel="vers les partants"
                        onSelect={() => handleBenchCardSelect(driver as Player | undefined)}
                        onRemove={
                          ((isTransfersState && restrictActiveTransfers && driver?.active === 1) || (isTransfersState && transferLimitReached)) ?
                            undefined :
                          driver && canEditRoster
                            ? () => handleRemoveDriver(driver as Player)
                            : undefined
                        }
                      />
                    ))}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
      
      {/* Right column: Player List and Calendar */}
      <div className="rounded-b-lg">
        <div className={`bg-[rgb(0_61_96)]  text-white p-4 rounded-t-lg`}>
          <h1 className="font-extra">Liste des coureurs</h1>
        </div>
        <div className='bg-white pt-4'>
          <div className="overflow-y-auto flex-1">
            <PlayerList
              containerId={PLAYER_LIST_ID}
              clubs={competitionData.clubs}
              drivers={competitionData.players}
              selectionType={'fantasyPick'}
              onSelect={handleDriverSelection}
              onRemove={handleRemoveDriver}
              isEditable={canEditRoster}
              isTransferMode={isTransfersState}
              selectionCheck={(driver) => driverSelectionCheck(driver, undefined, undefined, isTransfersState, effectiveAvailableBudget)}
            />
          </div>
        </div>
        {/* <div className="w-full mx-auto p-0 ">
          <a href='https://www.12gobiking.nl/?utm_source=ad.nl&utm_medium=banner&utm_campaign=wielerspel_giro' target='_blank'>
            <img src='https://dpgnl.s3.eu-west-1.amazonaws.com/files/klas-banner-12go.jpg' alt='12go-banner' />
          </a>
        </div> */}
        {/* <div className={`bg-[rgb(0_61_96)]  text-white p-4`}>
          <h1 className="font-extra">Profil de {copy.raceLabelWithArticle}</h1>
        </div>
        <div className='bg-white'>
          <div className="overflow-y-auto flex-1">
            <Image
              src={`https://ocnc-fantasy.b-cdn.net/files/profiles/${weekId}.jpg`}
              alt={`Profil de ${copy.raceLabelWithArticle} ${weekId}`}
              width={100}
              height={100}
              className="w-1/2 h-auto object-contain mx-auto"
              loading="lazy"
            />
            <a href='https://www.rtbf.be/article/tour-de-france-2025-les-profils-des-21-etapes-analyses-par-cyril-saugrain-11560147' className="relative block w-1/2 mx-auto m-4">
              <Image
                src={`https://ds.static.rtbf.be/article/image/770x433/c/3/c/89fab82ff0cc8969dad9e5ef3e4ae64e-1750789370.jpg`}
                alt={`Cyril Saugrain détaille les ${copy.raceLabelPlural}`}
                width={100}
                height={100}
                className="w-full h-auto object-contain"
                loading="lazy"
              />
              <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-40">
                <p className="text-white text-center font-semibold text-sm sm:text-base md:text-lg px-4">
                  Cyril Saugrain détaille les {copy.raceLabelPlural}
                </p>
              </div>
            </a>
          </div>
        </div> */}
      </div>

      
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 my-4">
      
      {/* Left column */}
      <div className="bg-white">
        
        <div className={`bg-[rgb(0_61_96)]  text-white p-4`}>
          <h1 className="font-extra">Actu Fantasy</h1>
        </div>
        <div className='bg-white pt-4'>
          <div className="overflow-y-auto flex-1">
            <Actus feedUrl="https://bff-service.rtbf.be/feed/v1/articles/on-connait-nos-classiques-le-jeu/json"/>
          </div>
        </div>
      </div>
      <div className="bg-white">
        
        <div className={`bg-[rgb(0_61_96)]  text-white p-4`}>
          <h1 className="font-extra">Actu Cyclisme</h1>
        </div>
        <div className='bg-white pt-4'>
          <div className="overflow-y-auto flex-1">
            <Actus feedUrl="https://bff-service.rtbf.be/feed/v1/articles/tour-de-france/json"/>
          </div>
        </div>
      </div>
      

      
    </div>

    {/* Sticky save bar */}
    {isSelectionsState && lineupHasChanges && (
      <div className="fixed bottom-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-t border-gray-200 shadow-[0_-4px_20px_rgba(0,0,0,0.1)] px-4 py-3 animate-in slide-in-from-bottom-2">
        <div className="container mx-auto flex items-center justify-between gap-3 max-w-3xl">
          <span className="text-sm font-semibold text-gray-600 hidden sm:block">
            Tu as des changements non enregistrés
          </span>
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <Button
              onClick={() => {
                const initial = initialLineupRef.current;
                if (!initial) return;
                const allPlayers = [...team.starting, ...team.bench];
                const restoredStarting = initial.startingIds
                  .map((id) => allPlayers.find((p) => p.id === id))
                  .filter(Boolean) as Player[];
                const restoredBench = initial.benchIds
                  .map((id) => allPlayers.find((p) => p.id === id))
                  .filter(Boolean) as Player[];
                updateLineup(
                  restoredStarting.map((p) => ({ ...p, pos: 1 })),
                  restoredBench.map((p) => ({ ...p, pos: 0 }))
                );
                setLineupSelection(null);
                toast.info('Changements annulés.');
              }}
              variant="secondary"
              className="font-bold rounded-full px-5 py-3 text-sm transition duration-200"
            >
              <XCircle className="w-4 h-4 mr-1.5" />
              Annuler
            </Button>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-2 shadow-sm" title="Choisis la portée de la sauvegarde">
                    <Flag
                      className={`h-4 w-4 transition-colors ${
                        saveFutureWeeksSelection ? 'text-gray-400' : 'text-green-600'
                      }`}
                    />
                    <Switch
                      checked={saveFutureWeeksSelection}
                      onCheckedChange={setSaveFutureWeeksSelection}
                      aria-label="Basculer entre sauvegarde d’une course ou de toute la saison"
                      className="data-[state=checked]:bg-green-600 data-[state=unchecked]:bg-gray-300"
                    />
                    <InfinityIcon
                      className={`h-4 w-4 transition-colors ${
                        saveFutureWeeksSelection ? 'text-green-600' : 'text-gray-400'
                      }`}
                    />
                  </div>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-xs text-xs">
                  {saveFutureWeeksSelection
                    ? `Sauvegarde les sélections pour toutes les ${copy.raceLabelPlural} restantes.`
                    : `Sauvegarde uniquement ${copy.raceLabelWithArticle} n°${weekId}.`}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <Button
              onClick={performSelectionsUpdate}
              disabled={selectionSaveDisabled}
              variant="green"
              className="flex-1 sm:flex-none font-extrabold rounded-full px-8 py-3 text-sm disabled:bg-gray-400 disabled:cursor-not-allowed transition duration-200 shadow-md"
            >
              <Save className="w-5 h-5 mr-2" />
              ENREGISTRER
            </Button>
          </div>
        </div>
      </div>
    )}

    {isEditState && (
      <div className="fixed bottom-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-t border-gray-200 shadow-[0_-4px_20px_rgba(0,0,0,0.1)] px-4 py-3">
        <div className="container mx-auto flex items-center justify-center max-w-3xl">
          <Button
            onClick={performUpdateTeam}
            disabled={baseSaveDisabled}
            variant="green"
            className="w-full sm:w-auto font-extrabold rounded-full px-8 py-3 text-sm disabled:bg-gray-400 disabled:cursor-not-allowed transition duration-200 shadow-md"
          >
            <Save className="w-5 h-5 mr-2" />
            ENREGISTRER LES CHANGEMENTS
          </Button>
        </div>
      </div>
    )}
    </>
  );
};

export default EditTeam;
